import { Link, useParams } from "react-router-dom";
import { useClub, useNotices } from "../hooks/useClubInfo";
import { MarkerBadge, severityTone } from "../components/MarkerBadge";

const pillIcons = {
  tag: <path d="M20.6 12.4 12 21 3 12l8.6-8.6H20a1 1 0 0 1 1 1v7.6l-.4.4Z M15.5 8.5h.01" />,
  bell: <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />,
  trophy: <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4ZM7 6H4a3 3 0 0 0 3 5M17 6h3a3 3 0 0 1-3 5" />,
  pin: <path d="M12 21s7-6.5 7-11.5a7 7 0 1 0-14 0C5 14.5 12 21 12 21ZM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" strokeLinecap="round" strokeLinejoin="round" /></>,
};

function QuickPill({ to, label, icon }: { to: string; label: string; icon: keyof typeof pillIcons }) {
  return (
    <Link
      to={to}
      className="card card-interactive flex flex-shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium text-turf-800"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4 text-turf-600">
        {pillIcons[icon]}
      </svg>
      {label}
    </Link>
  );
}

export function ClubDashboard() {
  const { clubId } = useParams();
  const { club, loading } = useClub(clubId);
  const { notices } = useNotices(clubId);
  const latest = notices.slice(0, 2);

  return (
    <main className="min-h-dvh pb-24">
      <div className="hero-fairway px-5 pb-8 pt-7">
        {loading ? (
          <div className="flex items-center gap-3">
            <div className="skeleton h-12 w-12 rounded-full bg-white/10" />
            <div className="space-y-2">
              <div className="skeleton h-3 w-20 rounded bg-white/10" />
              <div className="skeleton h-5 w-40 rounded bg-white/10" />
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-md">
            <div className="flex items-center gap-3">
              {club?.logo ? (
                <img src={club.logo} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-white/20" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 font-display text-lg text-chalk-50 ring-1 ring-white/20">
                  {club?.name?.charAt(0)}
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-wider text-turf-100/70">Willkommen bei</p>
                <h1 className="font-display text-2xl font-semibold text-chalk-50">{club?.name}</h1>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mx-auto -mt-5 max-w-md px-5">
        <div className="animate-fade-up flex gap-2 overflow-x-auto pb-1">
          <QuickPill to={`/club/${clubId}/info?tab=preise`} label="Preise" icon="tag" />
          <QuickPill to={`/club/${clubId}/info?tab=news`} label="News" icon="bell" />
          <QuickPill to={`/club/${clubId}/info?tab=events`} label="Turniere" icon="trophy" />
          <QuickPill to={`/club/${clubId}/info?tab=kontakt`} label="Kontakt" icon="pin" />
          {club?.teeTimeUrl && (
            <QuickPill to={`/club/${clubId}/info?tab=startzeiten`} label="Startzeiten" icon="clock" />
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Link
            to={`/club/${clubId}/info`}
            className="card card-interactive animate-fade-up flex flex-col justify-between rounded-2xl bg-turf-700 p-5 text-chalk-50"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-7 w-7">
              <path d="M12 8h.01M11 12h1v5h1M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="mt-6 font-semibold">Club-Infos</span>
          </Link>
          <Link
            to={`/club/${clubId}/melden`}
            className="card card-interactive animate-fade-up flex flex-col justify-between rounded-2xl p-5"
            style={{ animationDelay: "60ms" }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-7 w-7 text-flag-600">
              <path d="M12 9v4m0 4h.01M10.3 3.9 2.6 17.5A1.5 1.5 0 0 0 4 19.8h16a1.5 1.5 0 0 0 1.4-2.3L13.7 3.9a1.5 1.5 0 0 0-2.6 0Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="mt-6 font-semibold text-ink-900">Problem melden</span>
          </Link>
        </div>

        <section className="mt-8">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-lg font-semibold text-turf-900">Aktuelles</h2>
            <Link to={`/club/${clubId}/info?tab=news`} className="text-sm font-medium text-turf-700">
              Alle News
            </Link>
          </div>
          <div className="mt-2 rule-scorecard" />

          {latest.length === 0 ? (
            <p className="mt-4 text-sm text-ink-400">Aktuell keine Mitteilungen.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {latest.map((n, i) => (
                <li key={n.id} className="animate-fade-up" style={{ animationDelay: `${120 + i * 60}ms` }}>
                  <Link
                    to={`/club/${clubId}/news/${n.id}`}
                    className="card card-interactive block rounded-2xl p-4"
                  >
                    <div className="flex items-center gap-2">
                      <MarkerBadge tone={severityTone(n.severity)} size="sm">
                        {n.severity}
                      </MarkerBadge>
                      <p className="truncate font-semibold text-ink-900">{n.title}</p>
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-sm text-ink-600">{n.text}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
